import { useMemo, useState } from 'react';
import type { BotDoc, StoryRunDoc } from '@spirits/shared';

// TEST INSPECTOR — a debug popup that lays bare a run's hidden steering data:
// every possible ending (with its requiredArcs) and every character's possible
// arcs + current drift. Not part of normal play; it's a window into what the
// player can't see.
interface Props {
  story: StoryRunDoc & { id: string };
  bots: (BotDoc & { id: string })[];
}

export function StoryInspector({ story, bots }: Props) {
  const [open, setOpen] = useState(false);

  // One row per character — prefer the current scene's bot (freshest drift),
  // else any bot of theirs (arcs are identical across scenes).
  const cast = useMemo(() => {
    const byKey = new Map<string, BotDoc & { id: string }>();
    for (const b of bots) {
      const cur = byKey.get(b.seedBotKey);
      if (!cur || b.sceneId === story.currentSceneId) byKey.set(b.seedBotKey, b);
    }
    return [...byKey.values()];
  }, [bots, story.currentSceneId]);

  // characterKey -> (arcId -> title), to render requiredArcs readably.
  const arcTitle = useMemo(() => {
    const m = new Map<string, Map<string, string>>();
    for (const c of cast) m.set(c.seedBotKey, new Map((c.arcs ?? []).map((a) => [a.id, a.title])));
    return m;
  }, [cast]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="inspect endings + arcs (testing)"
        className="text-[11px] uppercase tracking-widest text-violet-300/70 hover:text-violet-200 border border-violet-500/30 hover:border-violet-400/50 rounded px-2 py-1 leading-none"
      >
        🔬 arcs
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-lg text-slate-100 truncate">
                {story.title} <span className="text-slate-500 text-sm">— test inspector</span>
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-slate-500 hover:text-slate-200 text-lg leading-none flex-none"
                title="close"
              >
                ✕
              </button>
            </div>

            {/* Possible endings + the arcs each one requires */}
            <section className="space-y-2">
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">
                Possible endings ({story.endings.length})
              </h3>
              {story.endings.map((e) => (
                <div key={e.id} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                  <p className="text-sm text-slate-100">
                    {e.isCanon ? '★ ' : ''}
                    {e.title} <span className="text-slate-600">· {e.id}</span>
                    {story.endingId === e.id && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-400/80">
                        reached
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400 leading-snug mt-1">{e.summary}</p>
                  {e.requiredArcs && Object.keys(e.requiredArcs).length > 0 && (
                    <p className="text-[11px] text-violet-300/70 mt-1.5 leading-snug">
                      <span className="uppercase tracking-widest text-violet-400/60 mr-1.5">requires</span>
                      {Object.entries(e.requiredArcs)
                        .map(([k, arcId]) => `${k} → ${arcTitle.get(k)?.get(arcId) ?? arcId}`)
                        .join('   ·   ')}
                    </p>
                  )}
                </div>
              ))}
            </section>

            {/* Each character's possible arcs + current drift (◦ leaning / ● locked) */}
            <section className="space-y-2">
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">
                Cast &amp; possible arcs
              </h3>
              {cast.map((c) => (
                <div key={c.seedBotKey} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                  <p className="text-sm text-slate-100">
                    {c.displayName}{' '}
                    <span className="text-slate-600">
                      · {c.seedBotKey} · locks @ scene {c.arcLockScene}
                    </span>
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {(c.arcs ?? []).map((a) => {
                      const isCurrent = a.id === c.currentArcId;
                      return (
                        <li
                          key={a.id}
                          className={`text-xs leading-snug ${isCurrent ? 'text-violet-200' : 'text-slate-400'}`}
                        >
                          <span className="mr-1.5">{isCurrent ? (c.arcLocked ? '●' : '◦') : '·'}</span>
                          <span className="text-slate-200">{a.title}</span>{' '}
                          <span className="text-slate-600">({a.id})</span> — {a.description}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </section>
          </div>
        </div>
      )}
    </>
  );
}
